import { useEffect } from 'react';
import {
    Backdrop,
    Card,
    CircularProgress,
    Grid,
    Box,
    Paper,
    Divider,
} from '@mui/material';
import { Header } from '../common/Header';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import {
    GetPredictionModels,
    clearPredictionData,
} from '../../store/prediction-slice/prediction';
import { useTranslation } from 'react-i18next';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import PredictionsTable from './PredictionsTable';
import PredictionModel from './PredictionModel';
import PredictionModelSelection from './PredictionModelSelection';
import { useSearchParams } from 'react-router-dom'

interface MLPredictionProps {
    modelId?: string | null;
    modelName?: string | null;
    showModelSelection?: boolean
}

const MLPrediction: React.FC<MLPredictionProps> = ({ modelId, modelName, showModelSelection }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [searchParams] = useSearchParams()
    const idFromUrl = searchParams.get('id')
    const nameFromUrl = searchParams.get('name')

    const { predictionInProgress } = useAppSelector(
        (state: RootState) => state.prediction
    );

    useEffect(() => {
        dispatch(GetPredictionModels());
        return () => {
            dispatch(clearPredictionData());
        };
    }, [dispatch]);

    // If parent passed props, prefer those. Otherwise fall back to ?id=…&name=… from URL.
    const propModelId = modelId ?? idFromUrl
    const propModelName = modelName ?? nameFromUrl

    return (
        <Box sx={{ margin: '0 auto' }}>
            { 
                showModelSelection && 
                <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
                    <Header
                        title={t('PREDICTION_TITLE')}
                        helperIcon={
                            <RedirectIconButton to={'/help/4'} className="helper-icon">
                                <ContactSupportIcon />
                            </RedirectIconButton>
                        }
                    />
                    <Box sx={{ p: 1.5 }}>
                        <Box sx={{ mb: 0.5, fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {t('PREDICTIONS_SELECT_MODEL')}
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <PredictionModelSelection predictionDataId={propModelId ?? null} />
                    </Box>
                </Card> 
            }

            {(propModelId && propModelId) ? (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                            <Box sx={{ mb: 0.5, fontSize: '1.1rem', fontWeight: 'bold' }}>
                                {t('NEW_PREDICTION_TITLE')}
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <PredictionModel
                                predictionDataId={propModelId}
                                predictionDataName={propModelName}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                            <Grid container sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Grid item sx={{ width: '100%', overflowY: 'auto' }}>
                                    <PredictionsTable predictionDataId={propModelId} />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            ) : null}

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={predictionInProgress}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default MLPrediction;